import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, formatZAR, BACKEND_URL } from "@/lib/api";
import {
  Sparkles, Calculator as CalcIcon, ShieldCheck, AlertTriangle, Clock,
  CheckCircle2, ArrowRight, Download, FileText, Ban,
} from "lucide-react";
import LeadCaptureForm from "@/components/LeadCaptureForm";
import ProformaCTA from "@/components/ProformaCTA";
import CaptureGate from "@/components/CaptureGate";
import { toast } from "sonner";

export default function TransferCalculator() {
  const [value, setValue] = useState("2500000");
  const [propertyType, setPropertyType] = useState("freehold");
  const [hasBond, setHasBond] = useState(true);
  const [buyerType, setBuyerType] = useState("individual");
  const [firstTimeBuyer, setFirstTimeBuyer] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [showLead, setShowLead] = useState(false);

  const numValue = () => parseFloat(String(value).replace(/[^0-9.]/g, "")) || 0;

  const handleCalc = async (e) => {
    e?.preventDefault();
    setError("");
    setLoading(true);
    try {
      const num = numValue();
      if (!num || num <= 0) {
        setError("Enter a valid property value in Rand.");
        setLoading(false);
        return;
      }
      const { data } = await api.post("/calc/transfer-cost", {
        property_value: num,
        property_type: propertyType,
        has_bond: hasBond,
        first_time_buyer: firstTimeBuyer,
        buyer_type: buyerType,
      });
      setResult(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async ({ name, email, phone }) => {
    setDownloading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/calc/legal-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name, email, phone,
          property_value: numValue(),
          property_type: propertyType,
          has_bond: hasBond,
          first_time_buyer: firstTimeBuyer,
          buyer_type: buyerType,
        }),
      });
      if (!response.ok) {
        const txt = await response.text().catch(() => "");
        throw new Error(txt || "Report failed");
      }
      const portalToken = response.headers.get("X-EH-Portal-Token");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `EH-Inc-Legal-Report-R${numValue()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Legal report downloaded — your matter is open.");
      if (portalToken) {
        // Delay so the toast renders before navigating away
        setTimeout(() => { window.location.href = `/client/${portalToken}`; }, 1200);
      }
    } catch (err) {
      toast.error("Could not download report");
      throw err;
    } finally {
      setDownloading(false);
    }
  };

  const insight = result?.legal_insight;

  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-12 gap-6">
        {/* Form column */}
        <div className="lg:col-span-5 bg-zinc-900 text-white p-10 lg:p-12 flex flex-col justify-between min-h-[520px]">
          <div>
            <div className="flex items-center gap-2 text-overline text-zinc-400 mb-4">
              <CalcIcon className="h-4 w-4" /> Legal Transfer Engine
            </div>
            <h3 className="font-serif text-4xl lg:text-5xl font-medium leading-tight tracking-tight">
              Beyond cost —
              <br />
              <span className="text-zinc-400">know what's required.</span>
            </h3>
            <p className="mt-4 text-zinc-400 text-sm leading-relaxed max-w-sm">
              Full breakdown plus the legal requirements, blockers, risks and timeline for your specific transfer profile.
            </p>
          </div>

          <form onSubmit={handleCalc} className="mt-10 space-y-5">
            <div>
              <label className="block text-overline text-zinc-400">Property value (ZAR)</label>
              <div className="relative mt-1">
                <span className="absolute left-0 top-1/2 -translate-y-1/2 text-zinc-500 text-2xl font-serif">R</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  className="w-full bg-transparent border-b-2 border-zinc-700 focus:border-white pl-6 pr-2 py-3 text-3xl font-light outline-none transition-colors"
                  placeholder="2,500,000"
                  data-testid="calc-property-value"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <SegmentedField
                label="Property type"
                value={propertyType}
                onChange={setPropertyType}
                options={[
                  { v: "freehold", l: "Freehold" },
                  { v: "sectional", l: "Sectional" },
                ]}
                testid="calc-property-type"
              />
              <SegmentedField
                label="Bond required?"
                value={hasBond ? "yes" : "no"}
                onChange={(v) => setHasBond(v === "yes")}
                options={[
                  { v: "yes", l: "Yes" },
                  { v: "no", l: "Cash" },
                ]}
                testid="calc-has-bond"
              />
            </div>

            <SegmentedField
              label="Buyer type"
              value={buyerType}
              onChange={setBuyerType}
              options={[
                { v: "individual", l: "Individual" },
                { v: "trust", l: "Trust" },
                { v: "company", l: "Company" },
              ]}
              testid="calc-buyer-type"
            />

            {error && <div className="text-sm text-red-400" data-testid="calc-error">{error}</div>}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-white text-zinc-900 px-6 py-4 font-medium hover:bg-zinc-100 transition-colors disabled:opacity-50"
              data-testid="calc-submit"
            >
              {loading ? "Calculating…" : "Calculate & analyse →"}
            </button>
          </form>
        </div>

        {/* Result column */}
        <div className="lg:col-span-7">
          {!result ? (
            <div className="h-full min-h-[520px] border border-zinc-200 bg-white p-10 lg:p-12 grid place-items-center text-center">
              <div>
                <Sparkles className="h-8 w-8 text-zinc-300 mx-auto" />
                <p className="mt-4 text-zinc-500 max-w-sm mx-auto leading-relaxed">
                  Enter your property details to see the full cost breakdown plus a legal action plan — requirements, blockers, risks, timeline and next steps.
                </p>
              </div>
            </div>
          ) : (
            <div className="border border-zinc-200 bg-white">
              <div className="grid grid-cols-2 lg:grid-cols-3">
                <BentoCell label="Property value" value={formatZAR(result.property_value)} />
                <BentoCell label="Transfer duty (SARS)" value={formatZAR(result.transfer_duty)} highlight />
                <BentoCell label="Conveyancing fee" value={formatZAR(result.conveyancing_fee)} />
                <BentoCell label="VAT on fees" value={formatZAR(result.vat_on_fees)} />
                <BentoCell label="Deeds office" value={formatZAR(result.deeds_office_fee)} />
                <BentoCell label="Sundries / FICA" value={formatZAR(result.sundries)} />
              </div>
              <div className="bg-zinc-900 text-white p-8 flex flex-wrap items-end justify-between gap-4" data-testid="calc-total-block">
                <div>
                  <div className="text-overline text-zinc-400">Estimated total</div>
                  <div className="mt-2 font-serif text-5xl lg:text-6xl font-medium leading-none tracking-tight">
                    {formatZAR(result.total)}
                  </div>
                  <div className="mt-2 text-xs text-zinc-500">Rates: {result.rates_year}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <CaptureGate
                    onCapture={downloadReport}
                    title="Download your legal report"
                    description="We use these details to open your matter and save your report alongside it. POPIA-compliant."
                    submitLabel={downloading ? "Preparing…" : "Generate & download"}
                    testid="calc-download-capture"
                    trigger={
                      <button
                        disabled={downloading}
                        className="border border-zinc-700 text-zinc-200 px-3 py-2 text-xs font-medium hover:bg-zinc-800 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
                        data-testid="calc-download-report"
                      >
                        <Download className="h-3.5 w-3.5" /> {downloading ? "Preparing…" : "Download report"}
                      </button>
                    }
                  />
                  <ProformaCTA
                    calcInputs={{
                      property_value: numValue(),
                      property_type: propertyType,
                      has_bond: hasBond,
                      first_time_buyer: firstTimeBuyer,
                      buyer_type: buyerType,
                    }}
                    matterType="conveyancing"
                    label="Open your matter"
                    variant="primary"
                    testid="calc-start-transaction"
                  />
                </div>
              </div>
              <div className="p-6 text-xs text-zinc-500 border-t border-zinc-200 leading-relaxed">
                Based on SARS 2024/2025, Deeds Office and Law Society guideline rates. Not legal advice.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* LEGAL INSIGHT */}
      {insight && (
        <div className="space-y-6" data-testid="legal-insight">
          <StatusBanner status={insight.status} description={insight.status_description} />

          <div className="grid lg:grid-cols-2 gap-6">
            <InsightCard
              icon={<CheckCircle2 className="h-4 w-4" />}
              title="Mandatory Legal Requirements"
              subtitle="To complete this transfer, the following are legally required:"
              testid="insight-requirements"
            >
              <ul className="space-y-4">
                {insight.requirements.map((r, i) => (
                  <li key={i} className="border-l-2 border-zinc-900 pl-4">
                    <div className="font-medium text-sm">
                      {r.title}
                      {r.priority === "conditional" && (
                        <span className="ml-2 text-[10px] tracking-widest uppercase bg-zinc-100 text-zinc-600 px-1.5 py-0.5">conditional</span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-zinc-600 leading-relaxed">{r.detail}</p>
                    <Source text={r.source} />
                  </li>
                ))}
              </ul>
            </InsightCard>

            <InsightCard
              icon={<Ban className="h-4 w-4" />}
              title="Blocking Conditions"
              subtitle="Transfer cannot register until ALL of the following are obtained:"
              testid="insight-blockers"
              accent="warning"
            >
              <ul className="space-y-3">
                {insight.blockers.map((b, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="text-amber-700 mt-0.5">✕</span>
                    <div>
                      <div className="text-sm">{b.text}</div>
                      <Source text={b.source} />
                    </div>
                  </li>
                ))}
              </ul>
            </InsightCard>

            <InsightCard
              icon={<AlertTriangle className="h-4 w-4" />}
              title="Risk Warnings"
              subtitle={
                insight.risks.length
                  ? "Profile-specific risks that commonly cause delays:"
                  : "No elevated risks identified for this profile."
              }
              testid="insight-risks"
              accent="danger"
            >
              {insight.risks.length > 0 && (
                <ul className="space-y-3">
                  {insight.risks.map((r, i) => (
                    <li key={i} className="flex gap-3">
                      <span
                        className={`text-[10px] tracking-widest uppercase px-1.5 py-0.5 h-fit ${
                          r.level === "high"
                            ? "bg-red-100 text-red-700"
                            : r.level === "medium"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-zinc-100 text-zinc-700"
                        }`}
                      >
                        {r.level}
                      </span>
                      <div>
                        <div className="text-sm text-zinc-700">{r.text}</div>
                        <Source text={r.source} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </InsightCard>

            <InsightCard
              icon={<Clock className="h-4 w-4" />}
              title="Typical Timeline"
              subtitle={`Total estimate — ${insight.timeline_total}`}
              testid="insight-timeline"
            >
              <ol className="space-y-4">
                {insight.timeline.map((p, i) => (
                  <li key={i} className="grid grid-cols-12 gap-3">
                    <div className="col-span-1 font-mono text-xs text-zinc-400 pt-0.5">0{i + 1}</div>
                    <div className="col-span-11">
                      <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <div className="font-medium text-sm">{p.phase}</div>
                        <div className="text-xs text-zinc-500 font-mono">{p.weeks}</div>
                      </div>
                      <div className="mt-1 text-xs text-zinc-600 leading-relaxed">{p.items.join(" · ")}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </InsightCard>
          </div>

          <InsightCard
            icon={<ArrowRight className="h-4 w-4" />}
            title="Next Steps"
            subtitle="A legal-driven action plan for your matter:"
            testid="insight-next-steps"
            dark
          >
            <ol className="grid md:grid-cols-2 gap-4">
              {insight.next_steps.map((s) => (
                <li key={s.step} className="bg-zinc-800 p-4 border-l-2 border-white">
                  <div className="flex items-baseline gap-3">
                    <span className="font-serif text-3xl text-zinc-500 leading-none">{s.step}</span>
                    <span className="font-medium">{s.title}</span>
                  </div>
                  <div className="mt-2 text-sm text-zinc-400 leading-relaxed">{s.detail}</div>
                </li>
              ))}
            </ol>
            <div className="mt-8 flex flex-col gap-4" data-testid="cta-stack">
              {/* OTP — DOMINANT primary CTA (large, amber, first) */}
              <div className="bg-amber-50 border-l-4 border-amber-500 px-5 py-4 md:max-w-2xl" data-testid="otp-cta-context">
                <div className="text-overline text-amber-900 mb-1">Don&apos;t have an Offer to Purchase yet?</div>
                <div className="text-sm text-zinc-700 leading-relaxed">
                  Generate a <strong>legally-compliant OTP</strong> in minutes — full firm template fidelity,
                  FICA checklists for both parties, and a draft you can take straight to the seller.
                </div>
              </div>
              <a
                href={`/intake/otp?price=${numValue()}&property_type=${propertyType}`}
                className="inline-flex items-center justify-center gap-3 bg-amber-500 text-zinc-900 px-8 py-5 text-lg md:text-xl font-bold tracking-tight hover:bg-amber-400 transition-colors shadow-lg shadow-amber-500/20 w-full md:w-auto md:max-w-2xl"
                data-testid="generate-otp-cta"
              >
                <FileText className="h-6 w-6" />
                Generate my Offer to Purchase
                <ArrowRight className="h-5 w-5" />
              </a>
              <div className="text-[11px] text-zinc-500 -mt-1" data-testid="otp-cta-microcopy">
                ~90 seconds · no payment required · your draft saves to a private portal
              </div>

              {/* Secondary actions — outline, smaller, de-emphasised */}
              <div className="flex flex-wrap gap-2 text-xs">
                <ProformaCTA
                  calcInputs={{
                    property_value: numValue(),
                    property_type: propertyType,
                    has_bond: hasBond,
                    first_time_buyer: firstTimeBuyer,
                    buyer_type: buyerType,
                  }}
                  matterType="conveyancing"
                  label="Open your matter"
                  variant="ghost"
                  testid="next-steps-cta"
                />
                <CaptureGate
                  onCapture={downloadReport}
                  title="Download your legal report"
                  submitLabel={downloading ? "Preparing…" : "Generate & download"}
                  testid="cta-download-capture"
                  trigger={
                    <button
                      disabled={downloading}
                      className="inline-flex items-center gap-1.5 border border-zinc-700 text-zinc-400 px-3 py-2 text-xs hover:bg-zinc-800 hover:text-zinc-200 transition-colors disabled:opacity-50"
                      data-testid="cta-download-report"
                    >
                      <Download className="h-3.5 w-3.5" /> {downloading ? "Preparing…" : "Download report"}
                    </button>
                  }
                />
              </div>
            </div>
          </InsightCard>

          <p className="text-xs text-zinc-500 leading-relaxed max-w-3xl">
            {insight.disclaimer}
          </p>
        </div>
      )}

      <LeadCaptureForm
        open={showLead}
        onClose={() => setShowLead(false)}
        defaults={{ property_value: result?.property_value, source: "calculator" }}
      />
    </div>
  );
}

function BentoCell({ label, value, highlight }) {
  return (
    <div className={`p-6 border-r border-b border-zinc-200 ${highlight ? "bg-zinc-50" : ""}`}>
      <div className="text-overline text-zinc-500">{label}</div>
      <div className="mt-2 font-serif text-2xl lg:text-3xl font-medium tracking-tight">{value}</div>
    </div>
  );
}

function SegmentedField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-overline text-zinc-400">{label}</label>
      <div className="mt-2 grid grid-flow-col auto-cols-fr border border-zinc-700">
        {options.map((opt) => (
          <button
            key={opt.v}
            type="button"
            onClick={() => onChange(opt.v)}
            className={`text-xs py-2 px-2 transition-colors ${
              value === opt.v ? "bg-white text-zinc-900" : "text-zinc-400 hover:bg-zinc-800"
            }`}
            data-testid={`${testid}-${opt.v}`}
          >
            {opt.l}
          </button>
        ))}
      </div>
    </div>
  );
}

function StatusBanner({ status, description }) {
  return (
    <div className="bg-zinc-900 text-white p-8 flex flex-wrap items-center gap-6 justify-between" data-testid="insight-status">
      <div className="flex items-center gap-5">
        <div className="h-12 w-12 bg-[#0055FF] grid place-items-center">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <div>
          <div className="text-overline text-zinc-400">Status</div>
          <div className="font-serif text-3xl font-medium tracking-tight mt-1">{status}</div>
        </div>
      </div>
      <p className="text-sm text-zinc-300 leading-relaxed max-w-xl">{description}</p>
    </div>
  );
}

function InsightCard({ icon, title, subtitle, children, testid, accent, dark }) {
  const accentBg =
    accent === "warning"
      ? "bg-amber-50 border-amber-200"
      : accent === "danger"
      ? "bg-red-50 border-red-200"
      : dark
      ? "bg-zinc-900 text-white border-zinc-900"
      : "bg-white border-zinc-200";
  return (
    <div className={`border p-8 ${accentBg}`} data-testid={testid}>
      <div className={`flex items-center gap-2 text-overline ${dark ? "text-zinc-400" : "text-zinc-500"}`}>
        {icon} {title}
      </div>
      <p className={`mt-3 text-sm leading-relaxed ${dark ? "text-zinc-300" : "text-zinc-700"}`}>{subtitle}</p>
      <div className="mt-6">{children}</div>
    </div>
  );
}

function Source({ text }) {
  return (
    <div className="mt-1.5 inline-block text-[10px] font-mono tracking-wider text-zinc-500 italic">
      Source: {text}
    </div>
  );
}
